import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import socket from "../../utils/socket.js";
import { apiGet, apiPost } from "../../utils/api.js";
import { displayName } from "../../utils/displayName";
import { getRole } from "../../utils/authService.js";
import { useTeamFilter } from "../../utils/useTeamFilter";
import Skeleton from "../common/Skeleton.jsx";
import { PageHeader, DataGrid, Badge, FilterBar, PillSelect } from "../common/ui.jsx";
import {
  Monitor,
  Cpu,
  Smartphone,
  Shield,
  LogOut,
  Lock,
  Search,
  RefreshCw,
  Clock,
  Globe,
  WifiOff,
  Radar,
  CheckCircle2,
  AlertCircle,
  Eye,
  Activity,
  UploadCloud,
  Grid,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "../../utils/toast";
import "./devices.css";

const UPDATE_IDLE_STATES = new Set(["idle", "no_update", "success", "failed", "rollback_success", "rollback_failed", undefined]);
const STATUS_PRIORITY = { online: 0, away: 1, offline: 2 };
const PAGE_SIZE = 10;

function updateStateLabel(state) {
  const labels = {
    checking: "Checking…", update_available: "Found…",
    downloading: "Downloading…", verifying: "Verifying…",
    staging: "Staging…", installing: "Installing…",
    restarting: "Restarting…", already_running: "In Progress",
  };
  return labels[state] ?? null;
}

// ─── CCTV tile: one WebRTC stream per device ───────────────────
function CCTVTile({ agentId, label }) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionIdRef = useRef(null);
  const pendingCandidates = useRef([]);
  const rdSet = useRef(false);
  const [status, setStatus] = useState("Connecting…");
  const [live, setLive] = useState(false);

  useEffect(() => {
    let disposed = false;

    const closePeer = () => {
      try { pcRef.current?.close(); } catch { /* */ }
      pcRef.current = null;
    };

    const makePc = () => {
      const stun = import.meta.env.VITE_STUN_URL || "stun:stun.l.google.com:19302";
      const servers = [{ urls: stun }];
      const turnUrl = import.meta.env.VITE_TURN_URL;
      if (turnUrl) servers.push({ urls: Array.isArray(turnUrl) ? turnUrl : [turnUrl], username: import.meta.env.VITE_TURN_USERNAME || "", credential: import.meta.env.VITE_TURN_CREDENTIAL || "" });
      const pc = new RTCPeerConnection({ iceServers: servers });
      pc.ontrack = (e) => {
        const [stream] = e.streams || [];
        if (videoRef.current && stream) videoRef.current.srcObject = stream;
        setStatus("Live"); setLive(true);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && sessionIdRef.current)
          socket.emit("screen:ice_candidate", { sessionId: sessionIdRef.current, candidate: e.candidate });
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed" || pc.connectionState === "closed") {
          setStatus("Disconnected"); setLive(false);
        }
      };
      pcRef.current = pc;
      return pc;
    };

    const onOffer = async ({ sessionId, sdp }) => {
      try {
        if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
        sessionIdRef.current = sessionId;
        const pc = pcRef.current || makePc();
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        rdSet.current = true;
        while (pendingCandidates.current.length) {
          try { await pc.addIceCandidate(new RTCIceCandidate(pendingCandidates.current.shift())); } catch { /* */ }
        }
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("screen:answer", { sessionId, sdp: answer });
        setStatus("Negotiating…");
      } catch { setStatus("Error"); }
    };

    const onIce = async ({ sessionId, candidate }) => {
      if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
      if (!rdSet.current) { pendingCandidates.current.push(candidate); return; }
      try { await pcRef.current?.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* */ }
    };

    const onEnded = ({ sessionId }) => {
      if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
      setStatus("Ended"); setLive(false); closePeer();
    };

    makePc();
    socket.on("screen:offer", onOffer);
    socket.on("screen:ice_candidate", onIce);
    socket.on("screen:session_ended", onEnded);

    socket.emit("screen:view_request", { agentId, stealth: true }, (resp) => {
      if (disposed) return;
      if (!resp?.success) { setStatus("Unavailable"); return; }
      sessionIdRef.current = resp.sessionId;
      setStatus("Waiting for stream…");
    });

    return () => {
      disposed = true;
      socket.off("screen:offer", onOffer);
      socket.off("screen:ice_candidate", onIce);
      socket.off("screen:session_ended", onEnded);
      if (sessionIdRef.current)
        socket.emit("screen:end", { sessionId: sessionIdRef.current, reason: "viewer_unmounted" });
      try {
        if (videoRef.current?.srcObject) {
          videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
          videoRef.current.srcObject = null;
        }
      } catch { /* */ }
      closePeer();
      pendingCandidates.current = [];
      rdSet.current = false;
    };
  }, [agentId]);

  return (
    <div className="cctv-tile">
      <video ref={videoRef} autoPlay playsInline muted className="cctv-video" />
      {!live && <div className="cctv-status">{status}</div>}
      <div className="cctv-label">{label}</div>
    </div>
  );
}

// ─── CCTV full-screen grid overlay ────────────────────────────
function CCTVGrid({ systems, onClose }) {
  const online = systems.filter((s) => s.status === "online");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cctv-overlay">
      <div className="cctv-bar">
        <span className="cctv-bar-title">
          <Grid size={16} /> Screen Wall — {online.length} device{online.length !== 1 ? "s" : ""}
        </span>
        <button className="cctv-close" onClick={onClose}><X size={20} /></button>
      </div>
      {online.length === 0 ? (
        <div className="cctv-empty"><Monitor size={48} strokeWidth={1} /><p>No online devices to display.</p></div>
      ) : (
        <div className="cctv-grid">
          {online.map((s) => (
            <CCTVTile key={s.agentId} agentId={s.agentId} label={s.agentId} />
          ))}
        </div>
      )}
    </div>
  );
}

const Devices = () => {
  const [agents, setAgents] = useState([]); // current page only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlockedFeatures, setUnlockedFeatures] = useState({});
  const [updateMap, setUpdateMap] = useState({});     // agentId → { needsUpdate, latestVersion }
  const [updateStates, setUpdateStates] = useState({}); // agentId → state string
  const [refreshing, setRefreshing] = useState(false);
  // Distinguishes "first fetch ever" (show the full skeleton) from every
  // later refetch triggered by typing/filtering/paging — those must NOT
  // unmount the FilterBar, or the search input remounts fresh on every
  // keystroke and drops focus/the cursor out of the box mid-type.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [showCCTV, setShowCCTV] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Deep-linkable via ?search= (e.g. the topnav search landing here with a
  // specific device pre-filtered) — seeding both search and debouncedSearch
  // from it directly, not just `search`, so the very first fetch already
  // uses it instead of firing once unfiltered and again 350ms later once
  // the debounce effect below catches up.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, online: 0, upToDate: 0, outdatedOnline: 0 });
  // Full-fleet, lightweight (agentId + status) list — independent of whatever
  // page the table is on. Screen Wall's "nothing selected = show every online
  // device" default needs the whole fleet, not just the current page, so it
  // reads from this instead of `agents`. Refreshed on load and on Refresh
  // click; not kept live via socket (same staleness tolerance as the header
  // counts below — hit Refresh for up-to-the-second accuracy).
  const [fleetLite, setFleetLite] = useState([]);
  // Selected rows can span pages you've since navigated away from, so their
  // snapshot (just enough for the Screen Wall: agentId + status) is kept
  // here rather than re-derived from `agents`, which only ever holds the
  // current page.
  const [selectedSnapshots, setSelectedSnapshots] = useState(new Map());
  const { departments } = useTeamFilter();
  const navigate = useNavigate();
  const role = getRole();

  // The ?search= useState initializers above only seed state on first mount —
  // if Universal Search lands here a *second* time with a different
  // ?search= while Devices is already mounted (never unmounted in between),
  // the URL changes but those initializers don't re-run, so the grid
  // silently keeps filtering on the first device. Re-sync whenever the URL's
  // search param actually changes.
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    setSearch((prev) => (prev === urlSearch ? prev : urlSearch));
    setDebouncedSearch((prev) => (prev === urlSearch ? prev : urlSearch));
    setPage(1);
  }, [searchParams]);

  // Debounce typed search before it hits the network, and land back on page 1
  // whenever the search term actually changes (batched with setDebouncedSearch
  // so the fetch effect below only fires once, not once per state change).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };
  const handleTeamFilterChange = (val) => {
    setTeamFilter(val);
    setPage(1);
  };

  const buildAgentsQuery = () => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (teamFilter !== "all") params.set("team", teamFilter);
    return params.toString();
  };

  // Only this page's rows are ever fetched — changing page, search, or a
  // filter re-requests just that slice instead of the whole fleet.
  useEffect(() => {
    let active = true;
    setLoading(true);
    apiGet(`/api/agents?${buildAgentsQuery()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data?.agents)) {
          setAgents(data.agents);
          setTotalPages(data.totalPages || 1);
          setTotal(data.total || 0);
          setCounts(data.counts || { total: 0, online: 0, upToDate: 0, outdatedOnline: 0 });
          setError(null);
        } else {
          setError("Infrastructure synchronization failed.");
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to fetch agents:", err);
        setError("Network communication error.");
      })
      .finally(() => { if (active) { setLoading(false); setHasLoadedOnce(true); } });
    return () => { active = false; };
  }, [page, debouncedSearch, statusFilter, teamFilter]);

  useEffect(() => {
    // ⭐ LISTEN FOR STABLE UPDATES — only touches an agent already on the
    // current page (or already selected); a brand new agent, or one that
    // belongs on a different page, shows up next time this page is
    // fetched (on Refresh or page change) rather than being spliced in.
    socket.on("agent_update", (updatedAgent) => {
      setAgents((prev) => {
        const idx = prev.findIndex((a) => a.agentId === updatedAgent.agentId);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...updatedAgent };
        return updated;
      });
      setSelectedSnapshots((prev) => {
        if (!prev.has(updatedAgent.agentId)) return prev;
        const next = new Map(prev);
        next.set(updatedAgent.agentId, { ...next.get(updatedAgent.agentId), ...updatedAgent });
        return next;
      });
    });

    // ⭐ AGENT CONNECTION ISSUE ALERT
    // Shown when an agent reconnects after being offline > 30 seconds.
    socket.on("agent_alert", (alert) => {
      const label = alert.hostname || alert.agentId;
      const secs = alert.offline_seconds || 0;
      const duration = secs >= 60
        ? `${Math.round(secs / 60)} min`
        : `${secs} sec`;
      toast.info(
        `${label} was offline for ${duration}. Buffered data is now syncing.`
      );
    });

    apiGet("/api/features")
      .then((res) => res.json())
      .then((data) => {
        const map = {};
        if (data?.unlockedFeatures) {
          data.unlockedFeatures.forEach((id) => (map[id] = true));
        }
        setUnlockedFeatures(map);
      })
      .catch((err) => console.error("Failed to fetch features:", err));

    apiGet("/api/visualizer-data")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setFleetLite(data);
          const map = {};
          data.forEach((a) => { map[a.agentId] = { needsUpdate: a.needsUpdate, latestVersion: a.latestVersion }; });
          setUpdateMap(map);
        }
      })
      .catch(() => { });

    const handleUpdateStatus = (data) => {
      if (!data?.agentId) return;
      setUpdateStates((prev) => ({ ...prev, [data.agentId]: data.state }));
    };
    socket.on("agent_update_status", handleUpdateStatus);

    return () => {
      socket.off("agent_update");
      socket.off("agent_alert");
      socket.off("agent_update_status", handleUpdateStatus);
    };
  }, []);

  const handleRefreshStatus = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      socket.emit("refresh_agent_states");
      const [agentRes, vizRes] = await Promise.all([
        apiGet(`/api/agents?${buildAgentsQuery()}`),
        apiGet("/api/visualizer-data"),
      ]);
      const [agentData, vizData] = await Promise.all([
        agentRes.json().catch(() => null),
        vizRes.json().catch(() => null),
      ]);
      // /api/visualizer-data only ever reports "online"/"offline" (30s heartbeat
      // window) — it has no concept of "away", so an agent's own reported status
      // (which does distinguish away) is kept unless the liveStatusMap says offline.
      // This preserves "away" across a refresh instead of silently wiping it.
      const liveStatusMap = {};
      if (Array.isArray(vizData)) {
        vizData.forEach((a) => { liveStatusMap[a.agentId] = a.status; });
        setFleetLite(vizData);
        const map = {};
        vizData.forEach((a) => { map[a.agentId] = { needsUpdate: a.needsUpdate, latestVersion: a.latestVersion }; });
        setUpdateMap(map);
      }
      if (Array.isArray(agentData?.agents)) {
        setAgents(agentData.agents.map((a) => {
          const live = liveStatusMap[a.agentId];
          if (live === "offline") return { ...a, status: "offline" };
          if (live === "online" && a.status === "offline") return { ...a, status: "online" };
          return a; // keep "away" (or any status not reported by visualizer-data) as-is
        }));
        setTotalPages(agentData.totalPages || 1);
        setTotal(agentData.total || 0);
        setCounts(agentData.counts || counts);
      }
      toast.success("Device statuses refreshed.");
    } catch {
      toast.error("Failed to refresh device statuses.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectionChange = (nextIds) => {
    setSelectedSnapshots((prev) => {
      const next = new Map(prev);
      nextIds.forEach((id) => {
        if (!next.has(id)) {
          const row = agents.find((a) => a.agentId === id);
          if (row) next.set(id, { agentId: row.agentId, status: row.status });
        }
      });
      Array.from(next.keys()).forEach((id) => {
        if (!nextIds.has(id)) next.delete(id);
      });
      return next;
    });
    setSelectedIds(nextIds);
  };

  const handleUpdateAll = () => {
    apiPost("/api/agents/update-all", {}).catch(() => { });
  };

  const handleUpdateOne = (agentId) => {
    apiPost(`/api/agents/${agentId}/update-now`, {}).catch(() => { });
  };

  const formatLastSeen = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  const canAccessDevices = role === "admin" || unlockedFeatures.devices;
  const canAccessTasks = canAccessDevices || unlockedFeatures.tasks;
  const canAccessApps = canAccessDevices || unlockedFeatures.apps;
  const canAccessPerformance = canAccessDevices || unlockedFeatures.performance;

  // The server already returns this page pre-sorted (status priority, then
  // most-recently-seen first) and pre-filtered by search/status/team — the
  // client no longer re-derives either. DataGrid's own column-header click
  // still re-sorts locally, scoped to whatever page is currently loaded.
  const hasActiveFilter = search.trim() !== "" || statusFilter !== "all" || teamFilter !== "all";

  const columns = [
    {
      key: "name",
      label: "Agent Name",
      sortable: true,
      sortFn: (a, b) => displayName(a).localeCompare(displayName(b)),
      render: (a) => (
        <div>
          <div style={{ fontWeight: 600 }}>{displayName(a)}</div>
          <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>{a.hostname || "—"}</div>
        </div>
      ),
    },
    {
      key: "version",
      label: "Version",
      sortable: true,
      render: (a) => {
        const outdated = updateMap[a.agentId]?.needsUpdate;
        return <span className={outdated ? "mono" : "mono"} style={outdated ? { color: "var(--color-warning)" } : undefined}>v{a.version || "0.0.0"}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortFn: (a, b) => (STATUS_PRIORITY[a.status] ?? 3) - (STATUS_PRIORITY[b.status] ?? 3),
      render: (a) => {
        if (a.status === "online") return <Badge tone="ok">Online</Badge>;
        if (a.status === "away") return <Badge tone="warn">Away</Badge>;
        return <Badge tone="bad">Offline</Badge>;
      },
    },
    {
      key: "lastSeen",
      label: "Last Seen",
      sortable: true,
      sortFn: (a, b) => new Date(a.lastSeen || 0).getTime() - new Date(b.lastSeen || 0).getTime(),
      render: (a) => <span className="mono">{formatLastSeen(a.lastSeen)}</span>,
    },
    {
      key: "actions",
      label: "",
      render: (a) => {
        const isOnline = a.status === "online";
        const info = updateMap[a.agentId] ?? {};
        const uState = updateStates[a.agentId];
        const canUpdate = info.needsUpdate && isOnline && UPDATE_IDLE_STATES.has(uState);
        const uLabel = updateStateLabel(uState);
        return (
          <div className="gc-row-actions">
            {(info.needsUpdate || uLabel) && isOnline && (
              <button
                className="gc-btn gc-btn-ghost gc-row-btn"
                onClick={(e) => { e.stopPropagation(); if (canUpdate) handleUpdateOne(a.agentId); }}
                style={!canUpdate ? { opacity: 0.6 } : undefined}
                title={uLabel ?? "Update"}
              >
                <UploadCloud size={13} /> <span className="gc-row-btn-label">{uLabel ?? "Update"}</span>
              </button>
            )}
            <button
              className="gc-btn gc-btn-ghost gc-row-btn"
              disabled={!isOnline}
              onClick={(e) => { e.stopPropagation(); navigate(`/screen/${a.agentId}`); }}
              title="View Screen"
            >
              <Eye size={13} /> <span className="gc-row-btn-label">View Screen</span>
            </button>
            <button
              className="gc-btn gc-btn-ghost gc-row-btn"
              onClick={(e) => { e.stopPropagation(); canAccessTasks ? navigate(`/tasks/${a.agentId}`) : navigate("/billing"); }}
              title="Task Manager"
            >
              {!canAccessTasks ? <Lock size={13} /> : <Cpu size={13} />} <span className="gc-row-btn-label">Task Manager</span>
            </button>
            <button
              className="gc-btn gc-btn-ghost gc-row-btn"
              onClick={(e) => { e.stopPropagation(); canAccessApps ? navigate(`/apps/${a.agentId}`) : navigate("/billing"); }}
              title="Software"
            >
              {!canAccessApps ? <Lock size={13} /> : <Smartphone size={13} />} <span className="gc-row-btn-label">Software</span>
            </button>
            <button
              className="gc-btn gc-btn-ghost gc-row-btn"
              onClick={(e) => { e.stopPropagation(); canAccessPerformance ? navigate(`/performance/${a.agentId}`) : navigate("/billing"); }}
              title="Performance"
            >
              {!canAccessPerformance ? <Lock size={13} /> : <Activity size={13} />} <span className="gc-row-btn-label">Performance</span>
            </button>
          </div>
        );
      },
    },
  ];

  const screenWallSystems = selectedIds.size > 0
    ? Array.from(selectedSnapshots.values())
    : fleetLite; // no selection = every online device across the whole fleet, not just this page

  return (
    <div className="devices-content-wrapper">
      <PageHeader
        title="Real-time status of agents"
        subtitle={!loading && !error ? `${counts.online}/${counts.total} online · ${counts.upToDate}/${counts.total} up to date` : undefined}
        actions={
          <>
            <button className="gc-btn gc-btn-ghost" onClick={() => setShowCCTV(true)}>
              <Grid size={13} /> Screen Wall{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
            </button>
            <button
              className="gc-icon-btn"
              data-tip="Refresh"
              onClick={handleRefreshStatus}
              disabled={refreshing}
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
            {counts.outdatedOnline > 0 && (
              <button className="gc-btn gc-btn-primary" onClick={handleUpdateAll}>
                <UploadCloud size={13} /> Update ({counts.outdatedOnline})
              </button>
            )}
          </>
        }
      />

      {loading && !hasLoadedOnce && (
        <div className="gc-card">
          <div className="gc-filter-bar">
            <Skeleton type="rect" width="230px" height="32px" style={{ borderRadius: 999 }} />
            <Skeleton type="rect" width="110px" height="32px" style={{ borderRadius: 999 }} />
          </div>
          <div style={{ padding: "10px 17px" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "11px 0", borderBottom: i < 6 ? "1px solid var(--border-subtle)" : "none" }}>
                <Skeleton type="rect" width="15px" height="15px" style={{ borderRadius: 4 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton type="title" width="160px" height="13px" />
                  <Skeleton type="text" width="110px" height="10px" style={{ marginTop: 4 }} />
                </div>
                <Skeleton type="text" width="60px" height="12px" />
                <Skeleton type="rect" width="70px" height="20px" style={{ borderRadius: 999 }} />
                <Skeleton type="text" width="70px" height="12px" />
                <Skeleton type="rect" width="140px" height="24px" style={{ borderRadius: 7 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="gc-empty">
          <AlertCircle size={44} />
          <p>{error}</p>
        </div>
      )}

      {/* Selection is what drives which devices the Screen Wall opens with, so
          say so explicitly rather than leaving the count on the button alone. */}
      {hasLoadedOnce && !error && selectedIds.size > 0 && (
        <div className="devices-selection-bar">
          <span className="devices-selection-count">
            <Grid size={13} />
            {selectedIds.size} device{selectedIds.size !== 1 ? "s" : ""} selected for Screen Wall
          </span>
          <button className="gc-btn gc-btn-ghost" onClick={() => setShowCCTV(true)}>
            Open
          </button>
          <button className="devices-selection-clear" onClick={() => setSelectedIds(new Set())}>
            Clear
          </button>
        </div>
      )}

      {hasLoadedOnce && !error && (
        <div className="gc-card">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, hostname, or version…"
          >
            <PillSelect value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)}>
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="away">Away</option>
              <option value="offline">Offline</option>
            </PillSelect>
            {departments.length > 0 && (
              <PillSelect value={teamFilter} onChange={(e) => handleTeamFilterChange(e.target.value)}>
                <option value="all">All Teams</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept.name}>{dept.name}</option>
                ))}
              </PillSelect>
            )}
          </FilterBar>
          <DataGrid
            columns={columns}
            rows={agents}
            getRowId={(a) => a.agentId}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onRowClick={(a) => navigate(`/devices/${a.agentId}`)}
            emptyState={
              hasActiveFilter
                ? <><Search size={40} style={{ opacity: 0.2 }} /><p>No devices match the current search/filter.</p></>
                : <><Monitor size={48} style={{ opacity: 0.2 }} /><p>No nodes registered in this network.</p></>
            }
          />
          {total > 0 && (
            <div className="devices-pagination">
              <span className="devices-pagination-count">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} device{total !== 1 ? "s" : ""}
              </span>
              <div className="devices-pagination-controls">
                <button
                  className="gc-icon-btn"
                  data-tip="Previous page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="devices-pagination-page">{page} / {totalPages}</span>
                <button
                  className="gc-icon-btn"
                  data-tip="Next page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showCCTV && (
        <CCTVGrid systems={screenWallSystems} onClose={() => setShowCCTV(false)} />
      )}
    </div>
  );
};

export default Devices;
